/**
 * centralBillingApi — Bearer-only billing reads/writes against Central.
 *
 * Central is the SOLE authority for subscription AND credit balance.
 *   GET  /api/subscription/me          → SubscriptionInfo
 *   GET  /api/credits/balance          → { available, reserved, total }
 *   POST /api/subscriptions/portal     → PortalSessionResponse  { url }  (body { return_to })
 *
 * All paths come from @levelup/central-billing constants. No local
 * BeHeard billing endpoints are called from the settings tab — Central
 * is the single source of truth. A balance Central did not state (any non-OK
 * answer, an unreadable body, a network failure) is UNKNOWN (null) — never
 * shown as 0 credits.
 *
 * Tolerant response parsing: Central may wrap data in { data: ... } or
 * { subscription: ... } / { wallet: { balance } } etc.
 */

import {
  SUBSCRIPTION_ME_PATH,
  SUBSCRIPTION_PORTAL_PATH,
  type SubscriptionInfo,
  type PortalSessionResponse,
} from '@levelup/central-billing';
import { CENTRAL_URL } from '../config/centralAuth';
import { throwClassifiedCentralFailure } from '../auth/reportCentralFailure';

/**
 * Central's credit balance read: `GET /api/credits/balance` → `{ ok, available,
 * reserved, total }` (routes/api/credits.ts). The installed
 * `@levelup/central-billing@1.0.0` exports `WALLET_BALANCE_PATH =
 * '/api/wallet/balance'`, a path Central does not serve: every read 404'd, so
 * Settings → Billing could only ever say "credit balance unavailable". Use the
 * real path here until the shared constant is corrected.
 */
export const CREDIT_BALANCE_PATH = '/api/credits/balance';

function authHeaders(token: string, extra: Record<string, string> = {}): Record<string, string> {
  return {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
    ...extra,
  };
}

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) {
    // THE choke point for every browser-direct Central call in this module. It
    // used to throw a plain Error, which the UI turned into generic "Failed to
    // load …" copy for a terminal 401 — with no login transition at all, because
    // nothing classified the answer.
    await throwClassifiedCentralFailure(res, 'centralBillingApi');
  }
  return res.json();
}

/** Pull a single record out of a `{ data: ... }` / `{ <key>: ... }` envelope. */
function extractRecord<T>(body: unknown, key: string): T | null {
  if (!body || typeof body !== 'object') return null;
  const obj = body as Record<string, unknown>;
  if (obj[key] && typeof obj[key] === 'object') return obj[key] as T;
  if (obj.data && typeof obj.data === 'object') {
    const data = obj.data as Record<string, unknown>;
    if (data[key] && typeof data[key] === 'object') return data[key] as T;
    return data as unknown as T;
  }
  return obj as unknown as T;
}

/**
 * GET /api/subscription/me
 * Returns null when Central STATES there is no subscription on file (404, or an
 * explicit `{ subscription: null }`). A failed read throws instead (readJson),
 * so null is never "could not read".
 */
export async function getSubscription(token: string): Promise<SubscriptionInfo | null> {
  const res = await fetch(`${CENTRAL_URL}${SUBSCRIPTION_ME_PATH}`, {
    headers: authHeaders(token),
  });
  if (res.status === 404) return null;
  const body = await readJson(res);
  if (body && typeof body === 'object') {
    const obj = body as Record<string, unknown>;
    const data = obj.data && typeof obj.data === 'object' ? (obj.data as Record<string, unknown>) : null;
    if (obj.subscription === null || data?.subscription === null) return null;
  }
  return extractRecord<SubscriptionInfo>(body, 'subscription');
}

/**
 * GET /api/credits/balance (CREDIT_BALANCE_PATH)
 * The spendable balance Central states, or null when it is unknown: a non-OK answer
 * (404 included — "no wallet" is not a stated balance), an unreadable body,
 * or a network failure. Never 0 for "could not read".
 * Reads `available` (Central's spendable figure) or a `balance` field, tolerant of
 * { data: … } | { wallet: … } wrappers.
 */
export async function getWalletBalance(token: string): Promise<number | null> {
  try {
    const res = await fetch(`${CENTRAL_URL}${CREDIT_BALANCE_PATH}`, {
      headers: authHeaders(token),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as Record<string, unknown> | null;
    if (!body) return null;
    const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
    const stated = (rec: Record<string, unknown> | undefined): number | null =>
      rec ? (num(rec.balance) ?? num(rec.available)) : null;
    if (stated(body) !== null) return stated(body);
    const data = body.data as Record<string, unknown> | undefined;
    if (stated(data) !== null) return stated(data);
    const wallet = (body.wallet ?? data?.wallet) as Record<string, unknown> | undefined;
    if (stated(wallet) !== null) return stated(wallet);
    return null;
  } catch {
    return null;
  }
}

/**
 * POST /api/subscriptions/portal  body { return_to }
 * Returns the Stripe Customer Portal URL.
 *
 * Central's handler reads `return_to` (routes/api/subscription.ts) and runs it
 * through its return-URL allowlist. This used to send `{ returnUrl }`, which
 * Central never reads, so every portal session returned to Central's default
 * page instead of BeHeard.
 */
export async function createCentralPortalSession(
  token: string,
  returnTo: string,
): Promise<string | null> {
  const res = await fetch(`${CENTRAL_URL}${SUBSCRIPTION_PORTAL_PATH}`, {
    method: 'POST',
    headers: authHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ return_to: returnTo }),
  });
  const body = await readJson(res);
  // PortalSessionResponse is `{ url }`. Be tolerant of wrappers.
  const obj = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  if (typeof obj.url === 'string') return obj.url;
  const data = obj.data as Record<string, unknown> | undefined;
  if (data && typeof data.url === 'string') return data.url;
  // Some deployments call the field portalUrl
  if (typeof obj.portalUrl === 'string') return obj.portalUrl;
  if (data && typeof data.portalUrl === 'string') return data.portalUrl;
  return null;
}

/**
 * A display name for Central's plan code, or null when Central supplied none
 * (unknown — never assumed to be "Free").
 */
export function planNameFor(planCode: string | undefined): string | null {
  if (!planCode) return null;
  // Title-case the code as a safe default
  return planCode.charAt(0).toUpperCase() + planCode.slice(1);
}

export type { SubscriptionInfo, PortalSessionResponse };
